import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import {
  Users,
  Mail,
  Globe,
  Plus,
  Trash2,
  RefreshCw,
  Search,
  AlertCircle,
  Loader2,
  Building2,
  Shield,
  User,
  Package,
  Settings,
  ChevronRight,
  FileText,
  ShoppingBag,
  Tag,
  Factory,
} from 'lucide-react';
import { Modal } from './ui';
import { EmailTemplatePreview } from './EmailTemplatePreview';
import {
  getVendorUsers,
  getInvitations,
  getDomainWhitelist,
  inviteVendorUser,
  addDomainWhitelist,
  removeDomainWhitelist,
  cancelInvitation,
  resendInvitation,
  deactivateVendorUser,
  getVendorOrganizations,
  createVendor,
  deactivateVendorOrganization,
  updateVendorOrganization,
  markVendorAsSupplier,
  removeVendorSupplierStatus,
  type VendorUser,
  type Invitation,
  type DomainWhitelist,
  type Vendor,
  type VendorUserRole,
} from '../api';
import { AdminProductCatalog } from './AdminProductCatalog';
import { AdminCategoryCatalog } from './AdminCategoryCatalog';
import { AdminManufacturerCatalog } from './AdminManufacturerCatalog';

// Generic domains that should be blocked from whitelisting
const BLOCKED_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'msn.com',
  'yahoo.com',
  'yahoo.co.uk',
  'ymail.com',
  'aol.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'protonmail.com',
  'proton.me',
  'zoho.com',
  'mail.com',
  'gmx.com',
  'gmx.net',
  'fastmail.com',
  'tutanota.com',
  'hey.com',
];

export function AdminVendorManagement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEmailTemplates, setShowEmailTemplates] = useState(false);

  // Get selected vendor ID and view from URL
  const selectedVendorId = searchParams.get('vendor');
  const view = searchParams.get('view'); // 'products' | 'categories' | null

  // Find the selected vendor from the vendors list
  const selectedVendor = useMemo(() => {
    if (!selectedVendorId) return null;
    return vendors.find((v) => v.vendorId === selectedVendorId) || null;
  }, [selectedVendorId, vendors]);

  const fetchVendors = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getVendorOrganizations();
      if (response.status) {
        setVendors(response.data);
      }
    } catch {
      // Silent fail
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const filteredVendors = vendors.filter(
    (vendor) =>
      vendor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vendor.contactEmail.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectVendor = (vendorId: string) => {
    setSearchParams({ vendor: vendorId });
  };

  const handleBackToList = () => {
    setSearchParams({});
  };

  const handleVendorUpdated = () => {
    fetchVendors();
  };

  const handleViewProducts = () => {
    setSearchParams({ view: 'products' });
  };

  const handleViewCategories = () => {
    setSearchParams({ view: 'categories' });
  };

  const handleViewManufacturers = () => {
    setSearchParams({ view: 'manufacturers' });
  };

  const handleViewVendors = () => {
    setSearchParams({});
  };

  // If Manufacturers view is selected
  if (view === 'manufacturers') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <button
            onClick={handleViewVendors}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            ← Back to Vendors
          </button>
        </div>
        <AdminManufacturerCatalog />
      </div>
    );
  }

  // If Categories view is selected
  if (view === 'categories') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <button
            onClick={handleViewVendors}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            ← Back to Vendors
          </button>
        </div>
        <AdminCategoryCatalog />
      </div>
    );
  }

  // If Product Catalog view is selected
  if (view === 'products') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <button
            onClick={handleViewVendors}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            ← Back to Vendors
          </button>
        </div>
        <AdminProductCatalog />
      </div>
    );
  }

  // If a vendor is selected and found, show the detail view
  if (selectedVendorId && selectedVendor) {
    return (
      <VendorDetailView
        vendor={selectedVendor}
        onBack={handleBackToList}
        onUpdated={handleVendorUpdated}
      />
    );
  }

  // If vendor ID is in URL but vendor not found (still loading or invalid ID)
  if (selectedVendorId && !selectedVendor && isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  // If vendor ID in URL but not found after loading, redirect to list
  if (selectedVendorId && !selectedVendor && !isLoading) {
    // Invalid vendor ID, go back to list
    handleBackToList();
    return null;
  }

  // Otherwise show the vendor list
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Manage Vendors</h2>
            <p className="text-sm text-gray-500">
              Create and manage vendor organizations, users, and access
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleViewProducts}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
            >
              <ShoppingBag className="w-4 h-4 flex-shrink-0" />
              <span className="whitespace-nowrap">Product Catalog</span>
            </button>
            <button
              onClick={handleViewCategories}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
            >
              <Tag className="w-4 h-4 flex-shrink-0" />
              <span className="whitespace-nowrap">Categories</span>
            </button>
            <button
              onClick={handleViewManufacturers}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
            >
              <Factory className="w-4 h-4 flex-shrink-0" />
              <span className="whitespace-nowrap">Manufacturers</span>
            </button>
            <button
              onClick={() => setShowEmailTemplates(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
            >
              <FileText className="w-4 h-4 flex-shrink-0" />
              <span className="whitespace-nowrap">Email Templates</span>
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm sm:text-base"
            >
              <Plus className="w-4 h-4 flex-shrink-0" />
              <span className="whitespace-nowrap">Create Vendor</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="p-4 border-b border-gray-100">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search vendors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
          />
        </div>
      </div>

      {/* Vendor List */}
      <div className="p-4">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        )}

        {!isLoading && filteredVendors.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No vendors found</p>
            <p className="text-sm mt-1">Create a vendor to get started</p>
          </div>
        )}

        {!isLoading && filteredVendors.length > 0 && (
          <div className="space-y-3">
            {filteredVendors.map((vendor) => (
              <button
                key={vendor.vendorId}
                onClick={() => handleSelectVendor(vendor.vendorId)}
                className="w-full p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors text-left group"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-gray-600" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">{vendor.name}</h3>
                      <p className="text-sm text-gray-500 truncate">{vendor.contactEmail}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 flex-wrap">
                    {vendor.isSupplier && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs">
                        <Package className="w-3 h-3" />
                        Distributor
                      </span>
                    )}
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        vendor.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {vendor.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Refresh button */}
        {!isLoading && (
          <div className="mt-4 text-center">
            <button
              onClick={fetchVendors}
              className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" />
              Refresh
            </button>
          </div>
        )}
      </div>

      {/* Create Vendor Modal */}
      <CreateVendorModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          fetchVendors();
        }}
      />

      {/* Email Template Preview */}
      {showEmailTemplates && (
        <EmailTemplatePreview onClose={() => setShowEmailTemplates(false)} />
      )}
    </div>
  );
}

// ===== Vendor Detail View (Subpage with Breadcrumb) =====
type VendorDetailTab = 'settings' | 'users' | 'invitations' | 'domains';
const VALID_TABS: VendorDetailTab[] = ['settings', 'users', 'invitations', 'domains'];

interface VendorDetailViewProps {
  vendor: Vendor;
  onBack: () => void;
  onUpdated: () => void;
}

function VendorDetailView({ vendor, onBack, onUpdated }: VendorDetailViewProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get active tab from URL, default to 'settings'
  const tabParam = searchParams.get('tab');
  const activeTab: VendorDetailTab = VALID_TABS.includes(tabParam as VendorDetailTab)
    ? (tabParam as VendorDetailTab)
    : 'settings';

  const setActiveTab = (tab: VendorDetailTab) => {
    // Preserve vendor param, update tab
    setSearchParams({ vendor: vendor.vendorId, tab });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Breadcrumb Header */}
      <div className="p-4 border-b border-gray-200">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-3">
          <button
            onClick={onBack}
            className="text-gray-700 hover:text-gray-900 hover:underline flex items-center gap-1"
          >
            <Building2 className="w-4 h-4" />
            Manage Vendors
          </button>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span className="text-gray-600">{vendor.name}</span>
        </nav>

        {/* Vendor Info */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-6 h-6 text-gray-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">{vendor.name}</h2>
              <p className="text-sm text-gray-500 truncate">{vendor.contactEmail}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {vendor.isSupplier && (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                <Package className="w-4 h-4" />
                Distributor
              </span>
            )}
            <span
              className={`px-3 py-1.5 text-sm font-medium rounded-full ${
                vendor.isActive
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {vendor.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-gray-50 overflow-x-auto scrollbar-thin">
        <nav className="flex -mb-px px-4 min-w-0">
          <TabButton
            active={activeTab === 'settings'}
            onClick={() => setActiveTab('settings')}
            icon={<Settings className="w-4 h-4" />}
            label="Settings"
          />
          <TabButton
            active={activeTab === 'users'}
            onClick={() => setActiveTab('users')}
            icon={<Users className="w-4 h-4" />}
            label="Users"
          />
          <TabButton
            active={activeTab === 'invitations'}
            onClick={() => setActiveTab('invitations')}
            icon={<Mail className="w-4 h-4" />}
            label="Invitations"
          />
          <TabButton
            active={activeTab === 'domains'}
            onClick={() => setActiveTab('domains')}
            icon={<Globe className="w-4 h-4" />}
            label="Domains"
          />
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-4 sm:p-6">
        {activeTab === 'settings' && (
          <VendorSettingsTab vendor={vendor} onUpdated={onUpdated} />
        )}
        {activeTab === 'users' && <VendorUsersTab vendorId={vendor.vendorId} />}
        {activeTab === 'invitations' && (
          <VendorInvitationsTab vendorId={vendor.vendorId} vendorName={vendor.name} />
        )}
        {activeTab === 'domains' && (
          <VendorDomainsTab vendorId={vendor.vendorId} vendorName={vendor.name} />
        )}
      </div>
    </div>
  );
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

function TabButton({ active, onClick, icon, label }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
        active
          ? 'border-gray-900 text-gray-900'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

// ===== Vendor Settings Tab =====
interface VendorSettingsTabProps {
  vendor: Vendor;
  onUpdated: () => void;
}

function VendorSettingsTab({ vendor, onUpdated }: VendorSettingsTabProps) {
  const [name, setName] = useState(vendor.name);
  const [contactEmail, setContactEmail] = useState(vendor.contactEmail);
  const [description, setDescription] = useState(vendor.description || '');
  const [isSupplier, setIsSupplier] = useState(vendor.isSupplier || false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTogglingSupplier, setIsTogglingSupplier] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      await updateVendorOrganization(vendor.vendorId, {
        name,
        contactEmail,
        description: description || undefined,
      });

      setSuccess('Vendor updated successfully');
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update vendor');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleSupplier = async () => {
    try {
      setIsTogglingSupplier(true);
      setError(null);

      if (isSupplier) {
        await removeVendorSupplierStatus(vendor.vendorId);
      } else {
        await markVendorAsSupplier(vendor.vendorId);
      }

      setIsSupplier(!isSupplier);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update supplier status');
    } finally {
      setIsTogglingSupplier(false);
    }
  };

  const handleDeactivate = async () => {
    if (!confirm(`Are you sure you want to deactivate "${vendor.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      setError(null);
      await deactivateVendorOrganization(vendor.vendorId);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate vendor');
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Basic Info */}
      <div className="space-y-4">
        <h3 className="font-medium text-gray-900">Basic Information</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Organization Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
          <input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description (Optional)
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
          />
        </div>

        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
        >
          {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
          Save Changes
        </button>
      </div>

      {/* Supplier Status */}
      <div className="pt-6 border-t border-gray-200">
        <h3 className="font-medium text-gray-900 mb-4">Distributor Status</h3>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            <Package className={`w-5 h-5 ${isSupplier ? 'text-orange-500' : 'text-gray-400'}`} />
            <div>
              <p className="font-medium text-gray-900">
                {isSupplier ? 'This vendor is a distributor' : 'Not a distributor'}
              </p>
              <p className="text-sm text-gray-500">
                Distributors can receive quality complaints from stores
              </p>
            </div>
          </div>
          <button
            onClick={handleToggleSupplier}
            disabled={isTogglingSupplier}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              isSupplier
                ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            } disabled:opacity-50`}
          >
            {isTogglingSupplier ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isSupplier ? (
              'Disable'
            ) : (
              'Enable'
            )}
          </button>
        </div>
      </div>

      {/* Danger Zone */}
      {vendor.isActive && (
        <div className="pt-6 border-t border-gray-200">
          <h3 className="font-medium text-red-600 mb-4">Danger Zone</h3>
          <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="font-medium text-gray-900">Deactivate Vendor</p>
                <p className="text-sm text-gray-500">
                  This will prevent all users from accessing this vendor
                </p>
              </div>
              <button
                onClick={handleDeactivate}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {success}
        </div>
      )}

      {/* Meta Info */}
      <div className="pt-6 border-t border-gray-200 text-sm text-gray-500">
        <p>Created: {new Date(vendor.createdAt).toLocaleDateString()}</p>
        <p>Last Updated: {new Date(vendor.updatedAt).toLocaleDateString()}</p>
      </div>
    </div>
  );
}

// ===== Vendor Users Tab =====
interface VendorUsersTabProps {
  vendorId: string;
}

function VendorUsersTab({ vendorId }: VendorUsersTabProps) {
  const [users, setUsers] = useState<VendorUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getVendorUsers(vendorId);
      if (response.status) {
        setUsers(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDeactivate = async (email: string) => {
    if (!confirm(`Deactivate ${email}?`)) return;
    try {
      await deactivateVendorUser(email);
      fetchUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to deactivate user');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <AlertCircle className="w-4 h-4" />
        {error}
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>No users in this vendor</p>
        <p className="text-sm mt-1">Invite users from the Invitations tab</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{users.length} user(s)</p>
        <button
          onClick={fetchUsers}
          className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
        >
          <RefreshCw className="w-3 h-3" />
          Refresh
        </button>
      </div>

      {users.map((user) => (
        <div
          key={user.email}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border border-gray-200 rounded-lg"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-gray-500" />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-gray-900 truncate">
                {user.firstName} {user.lastName}
              </p>
              <p className="text-sm text-gray-500 truncate">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                user.vendorRole === 'admin'
                  ? 'bg-purple-100 text-purple-800'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {user.vendorRole === 'admin' ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
              {user.vendorRole === 'admin' ? 'Admin' : 'User'}
            </span>
            <span
              className={`px-2 py-1 text-xs rounded-full ${
                user.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {user.isActive ? 'Active' : 'Inactive'}
            </span>
            {user.isActive && (
              <button
                onClick={() => handleDeactivate(user.email)}
                className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                title="Deactivate"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ===== Vendor Invitations Tab =====
interface VendorInvitationsTabProps {
  vendorId: string;
  vendorName: string;
}

function VendorInvitationsTab({ vendorId, vendorName }: VendorInvitationsTabProps) {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const fetchInvitations = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getInvitations(vendorId);
      if (response.status) {
        setInvitations(response.data.filter((inv) => inv.status === 'pending'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invitations');
    } finally {
      setIsLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const handleResend = async (email: string) => {
    try {
      await resendInvitation(email);
      alert('Invitation resent successfully');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to resend invitation');
    }
  };

  const handleCancel = async (email: string) => {
    if (!confirm(`Cancel invitation for ${email}?`)) return;
    try {
      await cancelInvitation(email);
      fetchInvitations();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to cancel invitation');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-gray-500">{invitations.length} pending invitation(s)</p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={fetchInvitations}
            disabled={isLoading}
            className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
          >
            <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowInviteModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            Invite User
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      )}

      {!isLoading && invitations.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No pending invitations</p>
        </div>
      )}

      {!isLoading && invitations.length > 0 && (
        <div className="space-y-3">
          {invitations.map((invitation) => (
            <div
              key={invitation.email}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border border-gray-200 rounded-lg"
            >
              <div>
                <p className="font-medium text-gray-900">{invitation.email}</p>
                <p className="text-xs text-gray-400 mt-1">
                  Expires {new Date(invitation.expiresAt).toLocaleDateString()}
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => handleResend(invitation.email)}
                  className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded transition-colors"
                >
                  Resend
                </button>
                <button
                  onClick={() => handleCancel(invitation.email)}
                  className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Invite Modal */}
      <InviteUserModal
        isOpen={showInviteModal}
        vendorId={vendorId}
        vendorName={vendorName}
        onClose={() => setShowInviteModal(false)}
        onSuccess={() => {
          setShowInviteModal(false);
          fetchInvitations();
        }}
      />
    </div>
  );
}

// ===== Vendor Domains Tab =====
interface VendorDomainsTabProps {
  vendorId: string;
  vendorName: string;
}

function VendorDomainsTab({ vendorId, vendorName }: VendorDomainsTabProps) {
  const [domains, setDomains] = useState<DomainWhitelist[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchDomains = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getDomainWhitelist(vendorId);
      if (response.status) {
        setDomains(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load domains');
    } finally {
      setIsLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  const handleRemove = async (domain: string) => {
    if (!confirm(`Remove @${domain} from whitelist?`)) return;
    try {
      await removeDomainWhitelist(domain);
      fetchDomains();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove domain');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-gray-500">{domains.length} whitelisted domain(s)</p>
          <p className="text-xs text-gray-400 mt-1">
            Users with these email domains can sign up automatically
          </p>
        </div>
        <div className="flex flex-wrap gap-2 flex-shrink-0">
          <button
            onClick={fetchDomains}
            disabled={isLoading}
            className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
          >
            <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            Add Domain
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      )}

      {!isLoading && domains.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Globe className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No whitelisted domains</p>
        </div>
      )}

      {!isLoading && domains.length > 0 && (
        <div className="space-y-3">
          {domains.map((domain) => (
            <div
              key={domain.domain}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border border-gray-200 rounded-lg"
            >
              <div>
                <p className="font-medium text-gray-900">@{domain.domain}</p>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs mt-1 ${
                    domain.defaultVendorRole === 'admin'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  Default: {domain.defaultVendorRole === 'admin' ? 'Admin' : 'User'}
                </span>
              </div>
              <button
                onClick={() => handleRemove(domain.domain)}
                className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Domain Modal */}
      <AddDomainModal
        isOpen={showAddModal}
        vendorId={vendorId}
        vendorName={vendorName}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          fetchDomains();
        }}
      />
    </div>
  );
}

// ===== Create Vendor Modal =====
interface CreateVendorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function CreateVendorModal({ isOpen, onClose, onSuccess }: CreateVendorModalProps) {
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !contactEmail) {
      setError('Please enter a name and contact email');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await createVendor({ name, contactEmail, description: description || undefined });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create vendor');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setName('');
      setContactEmail('');
      setDescription('');
      setError(null);
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Vendor"
      size="md"
      footer={
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 py-2.5 px-4 border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="create-vendor-form"
            disabled={isSubmitting}
            className="flex-1 py-2.5 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Create
          </button>
        </div>
      }
    >
      <form id="create-vendor-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Organization Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Corp"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
          <input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="contact@acme.com"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description (Optional)
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of this vendor..."
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
            rows={3}
          />
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

// ===== Invite User Modal =====
interface InviteUserModalProps {
  isOpen: boolean;
  vendorId: string;
  vendorName: string;
  onClose: () => void;
  onSuccess: () => void;
}

function InviteUserModal({ isOpen, vendorId, vendorName, onClose, onSuccess }: InviteUserModalProps) {
  const [email, setEmail] = useState('');
  const [vendorRole, setVendorRole] = useState<VendorUserRole>('user');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setVendorRole('user');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter an email address');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await inviteVendorUser({ email, vendorId, vendorRole });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Invite User to ${vendorName}`}
      size="md"
      footer={
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 py-2.5 px-4 border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="invite-user-form"
            disabled={isSubmitting}
            className="flex-1 py-2.5 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Send Invitation
          </button>
        </div>
      }
    >
      <form id="invite-user-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@vendor.com"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="vendorRole"
                value="user"
                checked={vendorRole === 'user'}
                onChange={() => setVendorRole('user')}
                className="w-4 h-4 text-primary-600"
              />
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-sm">User</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="vendorRole"
                value="admin"
                checked={vendorRole === 'admin'}
                onChange={() => setVendorRole('admin')}
                className="w-4 h-4 text-primary-600"
              />
              <Shield className="w-4 h-4 text-purple-500" />
              <span className="text-sm">Admin</span>
            </label>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

// ===== Add Domain Modal =====
interface AddDomainModalProps {
  isOpen: boolean;
  vendorId: string;
  vendorName: string;
  onClose: () => void;
  onSuccess: () => void;
}

function AddDomainModal({ isOpen, vendorId, vendorName, onClose, onSuccess }: AddDomainModalProps) {
  const [domain, setDomain] = useState('');
  const [defaultVendorRole, setDefaultVendorRole] = useState<VendorUserRole>('user');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setDomain('');
      setDefaultVendorRole('user');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanDomain = domain.replace('@', '').toLowerCase().trim();

    if (!cleanDomain) {
      setError('Please enter a domain');
      return;
    }

    // Check for blocked generic domains
    if (BLOCKED_DOMAINS.includes(cleanDomain)) {
      setError(
        `Generic email domains like ${cleanDomain} are not allowed. Please use a company domain.`
      );
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await addDomainWhitelist({ domain: cleanDomain, vendorId, defaultVendorRole });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add domain');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Add Domain to ${vendorName}`}
      size="md"
      footer={
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 py-2.5 px-4 border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-domain-form"
            disabled={isSubmitting}
            className="flex-1 py-2.5 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Add Domain
          </button>
        </div>
      }
    >
      <form id="add-domain-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
          <div className="flex items-center">
            <span className="px-3 py-2.5 bg-gray-100 border border-r-0 border-gray-200 rounded-l-lg text-gray-500">
              @
            </span>
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value.replace('@', ''))}
              placeholder="company.com"
              className="flex-1 px-3 py-2.5 border border-gray-200 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
              required
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            All users with this email domain can request access
          </p>
          <p className="mt-1 text-xs text-orange-600">
            Generic domains (gmail.com, hotmail.com, etc.) are not allowed
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Default Role</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="defaultVendorRole"
                value="user"
                checked={defaultVendorRole === 'user'}
                onChange={() => setDefaultVendorRole('user')}
                className="w-4 h-4 text-primary-600"
              />
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-sm">User</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="defaultVendorRole"
                value="admin"
                checked={defaultVendorRole === 'admin'}
                onChange={() => setDefaultVendorRole('admin')}
                className="w-4 h-4 text-primary-600"
              />
              <Shield className="w-4 h-4 text-purple-500" />
              <span className="text-sm">Admin</span>
            </label>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
